// src/components/settings/ProfilePictureUpload.tsx
import React, { useState, useRef } from 'react'
import { Upload, X, Loader } from 'lucide-react'
import { Avatar } from '../ui/Avatar'
import {
  uploadAvatar,
  deleteAvatar,
  validateAvatarFile,
  getPlaceholderAvatar,
} from '../../services/avatarService'
import { updateAvatar } from '../../services/userProfileService'

export interface ProfilePictureUploadProps {
  user: {
    id: string
    first_name?: string
    last_name?: string
    email?: string
    avatar_url?: string
  }
  onAvatarUpdate?: (avatarUrl: string | null) => void
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  user,
  onAvatarUpdate,
}) => {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file
    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      setError(validation.error || 'Invalid file')
      return
    }

    // Show preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    // Upload file
    handleUpload(file)
  }

  const handleUpload = async (file: File) => {
    setUploading(true)
    setError(null)

    try {
      // Upload to storage
      const uploadResult = await uploadAvatar(user.id, file)

      if (!uploadResult.success || !uploadResult.url) {
        setError(uploadResult.error || 'Failed to upload avatar')
        setPreview(null)
        return
      }

      // Update user profile with new avatar URL
      const updateResult = await updateAvatar(user.id, uploadResult.url)

      if (!updateResult.success) {
        setError(updateResult.error || 'Failed to update profile')
        return
      }

      // Success - notify parent component
      if (onAvatarUpdate) {
        onAvatarUpdate(uploadResult.url)
      }

      setPreview(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar')
      setPreview(null)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove your profile picture?')) {
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Delete from storage
      await deleteAvatar(user.id)

      // Revert to placeholder avatar
      const placeholderUrl = getPlaceholderAvatar(
        user.first_name && user.last_name
          ? `${user.first_name}-${user.last_name}`
          : user.email
      )

      const updateResult = await updateAvatar(user.id, placeholderUrl)

      if (!updateResult.success) {
        setError(updateResult.error || 'Failed to update profile')
        return
      }

      // Success - notify parent component
      if (onAvatarUpdate) {
        onAvatarUpdate(placeholderUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete avatar')
    } finally {
      setUploading(false)
    }
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-6">
        {/* Avatar Preview */}
        <div className="relative">
          {preview ? (
            <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-brand-500">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <Avatar user={user} size="xl" />
          )}

          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <Loader className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <button
              onClick={triggerFileInput}
              disabled={uploading}
              className="btn-primary text-sm flex items-center space-x-2"
            >
              <Upload className="w-4 h-4" />
              <span>{user.avatar_url ? 'Change Photo' : 'Upload Photo'}</span>
            </button>

            {user.avatar_url && user.avatar_url.includes('storage') && (
              <button
                onClick={handleDelete}
                disabled={uploading}
                className="btn-secondary text-sm flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Remove</span>
              </button>
            )}
          </div>

          <p className="mt-2 text-xs text-gray-500">
            JPG, PNG, WebP or GIF. Max size 2MB.
          </p>

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}
